import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { scheduledOrdersApi, ScheduledOrder } from "@/lib/api";
import { SortingState, PaginationState } from "@tanstack/react-table";
import { toast } from "sonner";
import { userManager } from "@/lib/oidcAuth";
import { useUser } from "@/contexts/UserContext";
import { resolveCycleYearFromCode } from "@/lib/orderCycle";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";
import { isOpenCycleOnlyScheduledOrdersEnabled } from "@/features/openCycleOnlyScheduledOrdersFeature";

export type ScheduledOrderFilter = {
  searchQuery: string;
  status: "all" | ScheduledOrder["status"];
  orderType: string;
  clientCode?: string;
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  deadlineRange?: {
    from?: Date;
    to?: Date;
  };
};

export function useScheduledOrderManagement(
  initialClientCode?: string,
  context: "backoffice" | "customer" = "customer",
) {
  const { openCycleYears, viewOnlyCycleYears, selectedCycleYear: contextCycleYear } = useUser();
  // Memoize storage keys to ensure they are stable per context
  const FILTERS_STORAGE_KEY = useMemo(
    () => `scheduled_order_filters_${context}_v2`,
    [context],
  );
  const PAGINATION_STORAGE_KEY = useMemo(
    () => `scheduled_order_pagination_${context}_v2`,
    [context],
  );

  // Use a ref to track current context to prevent saving stale data when context changes
  const contextRef = useRef(context);

  const [filters, setFilters] = useState<ScheduledOrderFilter>(() => {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        // Reconstruct Date objects if they exist
        if (parsed.dateRange) {
          if (parsed.dateRange.from)
            parsed.dateRange.from = new Date(parsed.dateRange.from);
          if (parsed.dateRange.to)
            parsed.dateRange.to = new Date(parsed.dateRange.to);
        }

        if (parsed.deadlineRange) {
          if (parsed.deadlineRange.from)
            parsed.deadlineRange.from = new Date(parsed.deadlineRange.from);
          if (parsed.deadlineRange.to)
            parsed.deadlineRange.to = new Date(parsed.deadlineRange.to);
        }

        return {
          ...parsed,
          clientCode: initialClientCode,
        };
      } catch (e) {
        console.error("Error loading saved filters:", e);
      }
    }
    return {
      searchQuery: "",
      status: "all",
      orderType: "all",
      clientCode: initialClientCode,
    };
  });

  // Handle context change: if the context changes, we MUST reload filters from its specific storage
  // or reset them to default to prevent state leakage between pages
  useEffect(() => {
    if (contextRef.current !== context) {
      contextRef.current = context;
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // (Date reconstruction logic omitted here for brevity, or just reset to empty for safety)
          setFilters({
            searchQuery: parsed.searchQuery || "",
            status: parsed.status || "all",
            orderType: parsed.orderType || "all",
            clientCode: initialClientCode,
            dateRange: parsed.dateRange
              ? {
                  from: parsed.dateRange.from
                    ? new Date(parsed.dateRange.from)
                    : undefined,
                  to: parsed.dateRange.to
                    ? new Date(parsed.dateRange.to)
                    : undefined,
                }
              : undefined,
            deadlineRange: parsed.deadlineRange
              ? {
                  from: parsed.deadlineRange.from
                    ? new Date(parsed.deadlineRange.from)
                    : undefined,
                  to: parsed.deadlineRange.to
                    ? new Date(parsed.deadlineRange.to)
                    : undefined,
                }
              : undefined,
          });
          return;
        } catch (e) {
          /* ignore */
        }
      }

      // Default reset if no saved data for new context
      setFilters({
        searchQuery: "",
        status: "all",
        orderType: "all",
        clientCode: initialClientCode,
      });
    }
  }, [context, FILTERS_STORAGE_KEY, initialClientCode]);

  // Sync clientCode separately
  useEffect(() => {
    setFilters((f) => ({ ...f, clientCode: initialClientCode }));
  }, [initialClientCode]);

  const [rawOrders, setRawOrders] = useState<ScheduledOrder[]>([]);
  const [selectedCycleYear, setSelectedCycleYear] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSend, setIsLoadingSend] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);

  const [pagination, setPagination] = useState<PaginationState>(() => {
    const saved = localStorage.getItem(PAGINATION_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          pageIndex: 0,
          pageSize: parsed.pageSize || 15,
        };
      } catch (e) {
        console.error("Error loading saved pagination:", e);
      }
    }
    return {
      pageIndex: 0,
      pageSize: 15,
    };
  });

  // Save filters effect
  useEffect(() => {
    // Only save if the context of the hook matches the current context
    if (contextRef.current === context) {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    }
  }, [filters, FILTERS_STORAGE_KEY, context]);

  // Save pagination effect
  useEffect(() => {
    if (contextRef.current === context) {
      localStorage.setItem(
        PAGINATION_STORAGE_KEY,
        JSON.stringify({ pageSize: pagination.pageSize }),
      );
    }
  }, [pagination.pageSize, PAGINATION_STORAGE_KEY, context]);

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [selectedCycleYear]);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await scheduledOrdersApi.getAll();
      setRawOrders((response.data || []).filter(Boolean));
      setError(null);
    } catch (e: any) {
      setError(e);
      toast.error("Erro ao carregar pedidos programados");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Ciclo ativo do sistema: mesma regra da tela de venda (OrderInterface) — considera apenas
  // ciclos Open dentre o ano atual e o próximo; prefere o ano corrente e, se ele não estiver
  // Open, cai para o primeiro ano Open disponível.
  const activeCycleYear = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const openYears = (openCycleYears || [])
      .filter((year) => year === currentYear || year === currentYear + 1)
      .sort((a, b) => a - b);
    if (openYears.length === 0) return null;
    return openYears.includes(currentYear) ? currentYear : openYears[0];
  }, [openCycleYears]);

  // Anos exibidos no seletor: sempre o ciclo anterior ao ativo e o ativo; o ciclo seguinte
  // só aparece quando existir (ciclo Open/Published configurado ou pedidos nele).
  const availableCycleYears = useMemo(() => {
    const activeYear = activeCycleYear ?? new Date().getFullYear();
    const previousYear = activeYear - 1;
    const nextYear = activeYear + 1;
    const configuredYears = [...(openCycleYears || []), ...(viewOnlyCycleYears || [])];
    const orderYears = rawOrders
      .map((order) => resolveCycleYearFromCode(order.cycleCode))
      .filter((year): year is number => Boolean(year));

    const years = new Set<number>([previousYear, activeYear]);
    [...configuredYears, ...orderYears].forEach((year) => {
      if (year === nextYear) years.add(year);
    });

    return [...years].sort((a, b) => a - b);
  }, [activeCycleYear, openCycleYears, rawOrders, viewOnlyCycleYears]);

  useEffect(() => {
    if (availableCycleYears.length === 0) return;

    setSelectedCycleYear((currentSelection) => {
      if (currentSelection && availableCycleYears.includes(currentSelection)) {
        return currentSelection;
      }
      // Regra temporária: sem escolha explícita, abre sempre no ciclo aberto (ver openCycleOnlyScheduledOrdersFeature).
      if (
        isOpenCycleOnlyScheduledOrdersEnabled
        && activeCycleYear
        && availableCycleYears.includes(activeCycleYear)
      ) {
        return activeCycleYear;
      }
      if (contextCycleYear && availableCycleYears.includes(contextCycleYear)) {
        return contextCycleYear;
      }
      return availableCycleYears[0];
    });
  }, [availableCycleYears, contextCycleYear, activeCycleYear]);

  const allOrders = useMemo(() => {
    const cycleOrders = selectedCycleYear
      ? rawOrders.filter(
          (order) => resolveCycleYearFromCode(order.cycleCode) === selectedCycleYear,
        )
      : rawOrders;

    if (!selectedCycleYear || selectedCycleYear < 2027) return cycleOrders;

    // A partir de 2027, o pedido de 2º semestre é único por escola/ciclo.
    // Enquanto dados anteriores à constraint definitiva ainda existirem, a tela usa
    // o registro mais recentemente atualizado como o representante da escola.
    const latestProgrammedOrderBySchool = new Map<string, ScheduledOrder>();
    const otherOrders: ScheduledOrder[] = [];

    cycleOrders.forEach((order) => {
      const isSecondSemester = String(order.cycleCode || "").endsWith("02");
      if (!isSecondSemester) {
        otherOrders.push(order);
        return;
      }

      const key = `${order.clientCode}:${selectedCycleYear}`;
      const current = latestProgrammedOrderBySchool.get(key);
      const orderTimestamp = new Date(order.updatedAt || order.createdAt).getTime();
      const currentTimestamp = current
        ? new Date(current.updatedAt || current.createdAt).getTime()
        : Number.NEGATIVE_INFINITY;

      if (!current || orderTimestamp > currentTimestamp) {
        latestProgrammedOrderBySchool.set(key, order);
      }
    });

    return [...latestProgrammedOrderBySchool.values(), ...otherOrders];
  }, [rawOrders, selectedCycleYear]);

  const sendOrders = async () => {
    const pendingOrders = allOrders.filter(
      (o) => o.status === "pending_confirmation" || o.status === "failed",
    );
    if (pendingOrders.length === 0) {
      toast.info("Não há pedidos pendentes para processar.");
      return;
    }

    setIsLoadingSend(true);
    let successCount = 0;
    let errorCount = 0;
    const toastId = toast.loading(
      `Processando ${pendingOrders.length} pedido(s) pendente(s)...`,
    );

    try {
      for (const order of pendingOrders) {
        try {
          await scheduledOrdersApi.sendIndividual(order.id);
          successCount++;
        } catch (e) {
          errorCount++;
        }
      }

      if (errorCount === 0) {
        toast.success(`Lote processado: ${successCount} pedidos concluídos!`, {
          id: toastId,
        });
      } else {
        toast.warning(`${successCount} processados, ${errorCount} falharam.`, {
          id: toastId,
        });
      }

      await fetchOrders();
    } catch (error) {
      toast.error("Erro ao processar lote!", { id: toastId });
    } finally {
      setIsLoadingSend(false);
    }
  };

  const sendIndividualOrder = async (orderId: string) => {
    setIsProcessingAction(true);
    try {
      await scheduledOrdersApi.sendIndividual(orderId);
      toast.success("Pedido enviado para processamento!");
      await fetchOrders();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Erro ao enviar pedido.";
      toast.error(message);
    } finally {
      setIsProcessingAction(false);
    }
  };

  const sendIndividualOrderClient = async (orderId: string, order) => {
    setIsProcessingAction(true);
    try {
      const user = await userManager.getUser();
      order.email = user?.profile.email || "";
      await scheduledOrdersApi.changeConfirm(orderId, order);
      toast.success("Pedido confirmado com sucesso!");
      await fetchOrders();
      return true;
    } catch (error: any) {
      // O backend bloqueia a confirmação e explica o motivo (ex.: faltam dados na tabela
      // de composição de avaliações/simulados).
      toast.error(error?.response?.data?.message || "Erro ao confirmar pedido!");
      return false;
    } finally {
      setIsProcessingAction(false);
    }
  };

  const reprocessSelectedOrders = async (orderIds: string[]) => {
    if (orderIds.length === 0) return;
    setIsProcessingAction(true);
    let successCount = 0;
    let errorCount = 0;

    const toastId = toast.loading(
      `Processando ${orderIds.length} pedido(s)...`,
    );

    try {
      for (const id of orderIds) {
        try {
          await scheduledOrdersApi.sendIndividual(id);
          successCount++;
        } catch (e) {
          errorCount++;
          console.error(`Erro ao processar pedido ${id}:`, e);
        }
      }

      if (errorCount === 0) {
        toast.success(`Todos os ${successCount} pedidos foram processados!`, {
          id: toastId,
        });
      } else {
        toast.warning(
          `${successCount} pedidos processados, ${errorCount} falharam.`,
          { id: toastId },
        );
      }

      await fetchOrders();
    } catch (error) {
      toast.error("Erro ao iniciar o processamento em lote.", { id: toastId });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const deleteSelectedOrders = async (orderIds: string[]) => {
    if (orderIds.length === 0) return;
    setIsProcessingAction(true);
    try {
      await scheduledOrdersApi.deleteOrders(orderIds);
      toast.success(`${orderIds.length} pedido(s) excluído(s) com sucesso!`);
      await fetchOrders();
    } catch (error) {
      toast.error("Erro ao excluir pedidos!");
    } finally {
      setIsProcessingAction(false);
    }
  };

  const filteredData = useMemo(() => {
    let data = allOrders;

    // Filter out completed orders for the backoffice list
    data = data.filter((order) => order?.status?.toLowerCase() !== "completed");

    if (filters.clientCode) {
      data = data.filter((order) => order?.clientCode === filters.clientCode);
    }

    if (filters.status !== "all") {
      data = data.filter((order) => {
        const orderStatus = (order?.status || "").toLowerCase().trim();
        const filterStatus = filters.status.toLowerCase().trim();

        // Simulados dos ciclos 2027+ seguem o fluxo do 2º semestre (status reais);
        // só os ciclos antigos mantêm o comportamento legado ("Programado").
        const isSimulado = `${order?.cycleCode}`.includes("00")
          && !isNewScheduledOrderCycleEnabledForCycle(
            Number(String(order?.cycleCode || "").slice(0, 4)),
          );

        if (filterStatus === "order_queue") {
          // Se for cancelado, não deve aparecer como "Programado"
          if (orderStatus === "cancelled") return false;
          return isSimulado || orderStatus === "order_queue";
        }

        // Se estivermos filtrando por cancelados, ignoramos a regra de ocultar simulados
        if (filterStatus === "cancelled") {
          return orderStatus === "cancelled";
        }

        if (isSimulado) return false;

        if (filterStatus === "pending_confirmation") {
          return (
            orderStatus === "pending_confirmation" || orderStatus === "pending"
          );
        }

        if (filterStatus === "confirmed") {
          return orderStatus === "confirmed" || orderStatus === "processed";
        }

        return orderStatus === filterStatus;
      });
    }

    if (filters.orderType !== "all") {
      data = data.filter((order) => {
        const isSimulado =
          `${order?.cycleCode}`.includes("00") ||
          (order?.orderNumber || "").startsWith("PED-SIM");

        if (filters.orderType === "programado") {
          return !isSimulado;
        }

        if (filters.orderType.startsWith("remessa-")) {
          const shipmentNum = filters.orderType.split("-")[1];
          return isSimulado && String(order.shipmentBlock) === shipmentNum;
        }

        if (filters.orderType === "simulado") {
          return isSimulado;
        }

        return true;
      });
    }

    if (filters.dateRange?.from) {
      const fromDate = new Date(filters.dateRange.from);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = filters.dateRange.to
        ? new Date(filters.dateRange.to)
        : new Date(fromDate);
      toDate.setHours(23, 59, 59, 999);

      data = data.filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= fromDate && orderDate <= toDate;
      });
    }

    if (filters.deadlineRange?.from) {
      const fromDate = new Date(filters.deadlineRange.from);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = filters.deadlineRange.to
        ? new Date(filters.deadlineRange.to)
        : new Date(fromDate);
      toDate.setHours(23, 59, 59, 999);

      data = data.filter((order) => {
        if (!order.deadline) return false;
        const orderDate = new Date(order.deadline);
        return orderDate >= fromDate && orderDate <= toDate;
      });
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      data = data.filter(
        (order) =>
          (order?.orderNumber ? String(order.orderNumber) : "")
            .toLowerCase()
            .includes(query) ||
          (order?.clientName ? String(order.clientName) : "")
            .toLowerCase()
            .includes(query) ||
          (order?.clientCode ? String(order.clientCode) : "")
            .toLowerCase()
            .includes(query) ||
          (order?.createdBy ? String(order.createdBy) : "")
            .toLowerCase()
            .includes(query),
      );
    }

    return data;
  }, [allOrders, filters]);

  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / pagination.pageSize);

  const tableData = useMemo(() => {
    const sortedData = [...filteredData];
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      sortedData.sort((a, b) => {
        const valA = a[id as keyof ScheduledOrder];
        const valB = b[id as keyof ScheduledOrder];

        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (typeof valA === "string" && typeof valB === "string") {
          return desc ? valB.localeCompare(valA) : valA.localeCompare(valB);
        }

        if (typeof valA === "number" && typeof valB === "number") {
          return desc ? valB - valA : valA - valB;
        }

        return 0;
      });
    }

    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;

    return sortedData.slice(start, end);
  }, [filteredData, sorting, pagination]);

  const handleClearFilters = () => {
    setFilters((prev) => ({
      searchQuery: "",
      status: "all",
      orderType: "all",
      clientCode: prev.clientCode,
      dateRange: undefined,
      deadlineRange: undefined,
    }));
    // Voltar ao ciclo aberto faz parte de limpar os filtros enquanto a regra temporária estiver ligada.
    if (isOpenCycleOnlyScheduledOrdersEnabled && activeCycleYear) {
      setSelectedCycleYear(activeCycleYear);
    }
  };

  return {
    tableData,
    totalRows,
    totalPages,
    isLoading,
    error,
    filters,
    setFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
    handleClearFilters,
    allOrders,
    sendOrders,
    isLoadingSend,
    sendIndividualOrder,
    sendIndividualOrderClient,
    reprocessSelectedOrders,
    deleteSelectedOrders,
    isProcessingAction,
    refreshData: fetchOrders,
    availableCycleYears,
    activeCycleYear,
    selectedCycleYear,
    setSelectedCycleYear,
  };
}
